import { useLocation } from "wouter";
import { KUALOA_PROPERTY_SLUG } from "@shared/property-context";
import {
  fallPromoForSlug,
  fallPromoPercentOff,
  isKualoaFallPromoVisible,
} from "@shared/promotions";
// SEO utilities for dynamic meta tags and structured data
import { useEffect, type ReactElement } from 'react';
import { Helmet } from 'react-helmet-async';
import { useQuery } from '@tanstack/react-query';
import {
  DEFAULT_CONTACT_PHONE,
  DEFAULT_CONTACT_PHONE_TEL_DISPLAY,
  formatContactEmail,
  formatContactPhone,
} from '@shared/contact';
import { useFeatureFlags } from "@/hooks/use-feature-flags";

function schemaTelephone(phone?: string | null): string {
  const formatted = formatContactPhone(phone);
  return formatted === DEFAULT_CONTACT_PHONE
    ? DEFAULT_CONTACT_PHONE_TEL_DISPLAY
    : formatted;
}

function schemaEmail(email?: string | null): string {
  return formatContactEmail(email);
}

const SITE_ORIGIN = 'https://vacationrentaloahu.co';
const getOrigin = () => (typeof window !== 'undefined' ? window.location.origin : SITE_ORIGIN);
/**
 * Canonical URL for the page currently being rendered.
 *
 * Derived from the router location rather than window.location, for two
 * reasons. During SSR there is no window, so the previous fallback returned the
 * bare site origin - meaning every server-rendered page that did not pass an
 * explicit `canonical` told crawlers it was a duplicate of the homepage.
 * Wouter's location works on both sides, because the SSR renderer drives the
 * Router with ssrPath.
 *
 * It also drops query strings, which window.location.href kept. A canonical
 * carrying ?utm_source=... points at a URL that is itself a duplicate, which is
 * the opposite of what the tag is for.
 */
function useCanonicalUrl(explicit?: string): string {
  const [routerPath] = useLocation();
  if (explicit) return explicit;
  const path = (routerPath || "/").split("?")[0].split("#")[0];
  const trimmed = path.length > 1 ? path.replace(/\/+$/, "") : path;
  return trimmed === "/" ? SITE_ORIGIN : `${SITE_ORIGIN}${trimmed}`;
}

// ---------------------------------------------------------------------------
// Canonical property facts for structured data (JSON-LD / Google Rich Results).
// Single-property site: these are the real, stored facts for the one rental.
// Kept here so every page's @graph is consistent even when the public API
// payload intentionally strips exact coordinates/address (see
// storage.getPropertyPublicDetails). streetAddress is deliberately NOT included
// because the exact street is not exposed anywhere else on the public site.
// ---------------------------------------------------------------------------
const PROPERTY_ID = '768ddc2d-04b5-47d2-88bc-9ed6ddfa6554';

// Real stored coordinates for the Ka'a'awa beach house (property.lat / property.lng).
export const PROPERTY_GEO = { latitude: 21.5136, longitude: -157.8401 } as const;

// Real gallery photos actually rendered on the site (from the photos table).
// Used as a fallback when a live property.photos array is unavailable (e.g. SSR
// before hydration). At least 8 distinct images so Google's "image must be
// defined at least 8 times" requirement is satisfied on every page.
const PROPERTY_IMAGE_FALLBACK: string[] = [
  'https://timberandlove.com/wp-content/uploads/2025/12/1-Oceanfront-Oahu-vacation-rental-exterior-with-direct-beach-access.png',
  'https://timberandlove.com/wp-content/uploads/2025/12/2-Oceanfront-Oahu-vacation-rental-exterior-with-direct-beach-access.png',
  'https://timberandlove.com/wp-content/uploads/2025/12/9-Spacious-living-room-in-Oahu-vacation-rental-with-ocean-views.png',
  'https://timberandlove.com/wp-content/uploads/2025/12/11-Modern-kitchen-in-Oahu-beach-house-vacation-rental.png',
  'https://timberandlove.com/wp-content/uploads/2025/12/12-Clean-bathroom-in-Oahu-vacation-rental.png',
  'https://timberandlove.com/wp-content/uploads/2025/12/13-Bright-bedroom-with-ocean-view-in-Oahu-Hawaii-vacation-rental.png',
  'https://timberandlove.com/wp-content/uploads/2025/12/18-Beachfront-Oahu-vacation-rental-with-sandy-shoreline-and-clear-water.png',
  'https://timberandlove.com/wp-content/uploads/2025/12/24-Oahu-beachfront-vacation-rental-near-Kualoa-Ranch-and-Chinamans-Hat-scaled.jpg',
  'https://timberandlove.com/wp-content/uploads/2025/12/25-Oahu-beachfront-vacation-rental-near-Kualoa-Ranch-and-Chinamans-Hat-scaled.jpg',
];

// Fallback aggregate rating, used only on pages that do not load live property data
// (currently /faq and /kaaawa). It MUST match what GET /api/reviews/summary returns,
// which is computed from approved rows in guest_reviews.
//
// Google requires AggregateRating to reflect the reviews actually available on the site.
// The previous value here (4.93 / 55) was not backed by any review records - there are 48
// approved reviews averaging 4.94 - so /faq and /kaaawa published a review count 15% higher
// than the homepage, which is both a structured-data policy risk and a contradictory fact
// for AI answer engines quoting the site.
//
// Re-check this against /api/reviews/summary whenever reviews are approved in bulk.
export const PROPERTY_RATING = { ratingValue: '4.94', reviewCount: 48 } as const;

// Real, publicly displayed guest reviews (guest_reviews table, featured+approved).
const PROPERTY_REVIEWS = [
  {
    author: 'Mary',
    location: 'Spring Branch, Texas',
    rating: 5,
    datePublished: '2025-07-15',
    body: 'Absolutely loved our stay! The location was peaceful and relaxing, the perfect place to unwind. The view was breathtaking, even more stunning than the photos. Our host was incredible, going above and beyond to make us feel welcome, even surprising us with delicious Hawaiian sweet treats. Highly recommend and would definitely stay here again!',
  },
  {
    author: 'Lang',
    location: 'Forest Grove, Oregon',
    rating: 5,
    datePublished: '2024-02-15',
    body: 'This is a beautiful Air BnB. We had 8 people staying and felt that we had plenty of room. The home is lovely and clean and private and the views were spectacular! We loved everything about it. Would highly recommend and would definitely stay here again.',
  },
] as const;

// Absolute-URL image list for the property: prefer the live photos array, fall
// back to the known real gallery. Always returns >= 8 absolute https URLs.
function getPropertyImages(property?: any): string[] {
  const toAbsolute = (u: string) =>
    u.startsWith('http') ? u : `${SITE_ORIGIN}${u.startsWith('/') ? '' : '/'}${u}`;
  const live: string[] = Array.isArray(property?.photos)
    ? property.photos
        .map((p: any) => (typeof p === 'string' ? p : p?.url))
        .filter((u: any): u is string => typeof u === 'string' && u.length > 0)
        .map(toAbsolute)
    : [];
  const merged = Array.from(new Set([...live, ...PROPERTY_IMAGE_FALLBACK]));
  return merged.length >= 8 ? merged : [...merged, ...PROPERTY_IMAGE_FALLBACK].slice(0, Math.max(8, merged.length));
}

// Real geo coordinates, preferring live property fields when present.
function getPropertyGeo(property?: any) {
  return {
    '@type': 'GeoCoordinates',
    latitude: property?.lat ? parseFloat(property.lat) : PROPERTY_GEO.latitude,
    longitude: property?.lng ? parseFloat(property.lng) : PROPERTY_GEO.longitude,
  };
}

/**
 * Stable identifier node for an entity (Google "identifier" field).
 *
 * The value is the entity's own @id, not one shared id for everything. Every
 * node in the graph used to carry `value: PROPERTY_ID`, so the Organization,
 * the Place, the LodgingBusiness and the VacationRental all claimed to be the
 * same thing. Once Google merged the two lodging nodes, `identifier` came back
 * as an array of two PropertyValues where one was expected, which is what
 * Search Console was reporting as "Invalid value type for field identifier".
 *
 * Shape follows Google's VacationRental reference exactly: @type, propertyID,
 * value, and nothing else. The `name` and `url` keys that used to be here are
 * not part of it.
 */
function buildIdentifier(entityId: string, propertyID = 'PropertyID') {
  return {
    '@type': 'PropertyValue',
    propertyID,
    value: entityId,
  };
}

// AggregateRating node from real stored rating/review counts.
/**
 * The nightly offers published as structured data.
 *
 * Two things were wrong here. The standard offer carried
 * `priceValidUntil: today + 30 days`, a date derived from nothing - the rate is
 * not known to change then, and publishing a validity window nobody set is the
 * same class of claim as an invented licence number. It is omitted now rather
 * than guessed: a missing optional field costs nothing, an untrue one costs
 * trust with the consumer that checks it.
 *
 * And the limited-time offer was invisible to machines. It was on the page and
 * in llms.txt but absent from the schema, so an assistant asked "any deals on
 * Oahu rentals?" had nothing structured to match. It is now its own Offer with
 * real `validFrom`, `validThrough` and `priceValidUntil` - the only dates here
 * that anyone actually set - and it disappears from the markup on the same day
 * it disappears from the page, because both read the same date gate.
 *
 * One builder, used by every schema that publishes a price, so the property
 * cannot advertise one rate in one graph node and a different rate in another.
 */
function buildPropertyOffers(
  pricing?: any,
  property?: any,
): Record<string, any> | Record<string, any>[] | undefined {
  const origin = getOrigin();

  // Prefer a real quote, fall back to the property's own current nightly rate.
  //
  // The fallback is the point. Every offer here used to be gated on `pricing`,
  // which the home page fed from a one-night quote - and one night is below the
  // four-night minimum, so that request returned 400 every time. `pricing` was
  // therefore always undefined, and the property published NO price in its
  // structured data at all. A lodging with no Offer is a lodging Google and every
  // answer engine cannot quote a rate for.
  //
  // `base_rate` comes from the property record, already resolved against the
  // seasonal rules for today, so it needs no quote and cannot 400.
  const resolved = pricing?.nightlyRate ?? pricing?.base_price ?? property?.base_rate;
  if (resolved === undefined || resolved === null || resolved === "") return undefined;
  const standardPrice = String(resolved);

  const standard: Record<string, any> = {
    "@type": "Offer",
    "url": `${origin}/booking`,
    "priceCurrency": "USD",
    "price": standardPrice,
    "priceSpecification": {
      "@type": "UnitPriceSpecification",
      "price": standardPrice,
      "priceCurrency": "USD",
      "unitText": "per night",
    },
    "availability": "https://schema.org/InStock",
    "seller": {
      "@type": "Organization",
      "name": BUSINESS_NAME,
    },
  };

  // Each home advertises only its own offer; a home without one publishes
  // just its standard rate.
  const fallPromo = fallPromoForSlug(property?.slug);
  if (!fallPromo || !isKualoaFallPromoVisible()) return standard;

  const promoPrice = fallPromo.promoNightly.toFixed(2);
  const promo: Record<string, any> = {
    "@type": "Offer",
    "name": `Limited-Time Fall Special at ${fallPromo.homeTitle}`,
    "description":
      `${fallPromoPercentOff(fallPromo)}% off the nightly rate for stays from ` +
      `September 14 through October 14, 2026. $${promoPrice} per night against a ` +
      `$${fallPromo.regularNightly} standard rate, before cleaning fee.`,
    "url": `${origin}/#check-availability`,
    "priceCurrency": "USD",
    "price": promoPrice,
    "priceSpecification": {
      "@type": "UnitPriceSpecification",
      "price": promoPrice,
      "priceCurrency": "USD",
      "unitText": "per night",
      "validFrom": fallPromo.startsOn,
      "validThrough": fallPromo.endsOn,
    },
    "availability": "https://schema.org/InStock",
    "validFrom": fallPromo.startsOn,
    "validThrough": fallPromo.endsOn,
    "priceValidUntil": fallPromo.endsOn,
    "seller": {
      "@type": "Organization",
      "name": BUSINESS_NAME,
    },
  };

  return [promo, standard];
}

function buildAggregateRating(property?: any) {
  const ratingValue = property?.rating || PROPERTY_RATING.ratingValue;
  const reviewCount =
    property?.review_count !== undefined && property?.review_count !== null
      ? property.review_count
      : PROPERTY_RATING.reviewCount;
  return {
    '@type': 'AggregateRating',
    ratingValue: String(ratingValue),
    reviewCount: String(reviewCount),
    bestRating: '5',
    worstRating: '1',
  };
}

// One or more real review nodes for embedding on the lodging entities.
function buildReviewNodes(limit = 2) {
  return PROPERTY_REVIEWS.slice(0, limit).map((r) => ({
    '@type': 'Review',
    author: { '@type': 'Person', name: r.author },
    reviewRating: { '@type': 'Rating', ratingValue: String(r.rating), bestRating: '5', worstRating: '1' },
    datePublished: r.datePublished,
    reviewBody: r.body,
  }));
}

/**
 * The amenities the rental actually has, as Google expects them.
 *
 * One list, used by the rental unit and by any other node that advertises
 * features, so the property cannot claim an amenity in one place and stay
 * silent about it in another. Neither home has a hot tub; do not add one.
 * Meta reads this markup into the "selling points" it shows on ads.
 */
const PROPERTY_AMENITIES: ReadonlyArray<readonly [string, string]> = [
  ['Private Beach Access', 'beachAccess'],
  ['Ocean View', 'oceanView'],
  ['Beachfront Location', 'beachfront'],
  ['Full Kitchen', 'kitchen'],
  ['WiFi', 'internet'],
  ['Free Parking', 'freeParking'],
  ['Air Conditioning', 'airConditioning'],
  ['Washer and Dryer', 'laundry'],
  ['BBQ Grill', 'barbecue'],
] as const;

function buildAmenityFeatures() {
  return PROPERTY_AMENITIES.map(([name, id]) => ({
    '@type': 'LocationFeatureSpecification',
    name,
    value: true,
    ...(id ? { propertyID: id } : {}),
  }));
}

/**
 * containsPlace: the rental unit itself, in the shape Google's VacationRental
 * documentation specifies.
 *
 * This used to be a list of one Accommodation per bedroom plus one Room per
 * bathroom, which produced four of the six critical errors in Search Console
 * at once. Google does not want a room inventory here. It wants a single
 * Accommodation describing the whole unit a guest books, which is why it kept
 * asking for `numberOfBedrooms` and `numberOfBathroomsTotal` "in containsPlace"
 * while the markup was busy naming "Bedroom 1" and "Bathroom 2". The per-room
 * occupancy also used `maxValue`, and the field Google reads is `value`, so
 * every one of those entries was reported as missing occupancy.value as well.
 *
 * `bed` and `floorSize` are deliberately absent. Google lists both as
 * optional-but-recommended, and neither exists in the property record: there
 * are columns for bedrooms, bathrooms and max_guests and nothing about bed
 * types or square footage. Publishing a guessed bed configuration to clear a
 * warning is the same class of claim as the invented `priceValidUntil` that
 * was removed from the offers above. If the real numbers are added to the
 * property, add them here.
 */
function buildContainsPlace(property?: any) {
  const bedrooms = property?.bedrooms || 3;
  const bathrooms = property?.bathrooms || 2;
  const maxGuests = property?.max_guests || 8;
  return {
    '@type': 'Accommodation',
    // Google's enum for a whole-home booking. The previous value here was a
    // schema.org URL, which is not one of the accepted values.
    additionalType: 'EntirePlace',
    name: property?.title || BUSINESS_NAME,
    occupancy: {
      '@type': 'QuantitativeValue',
      value: maxGuests,
      unitText: 'person',
    },
    numberOfBedrooms: bedrooms,
    numberOfBathroomsTotal: bathrooms,
    amenityFeature: buildAmenityFeatures(),
  };
}

export interface SEOProps {
  title?: string;
  description?: string;
  pageType?: string; // 'home' | 'booking' | 'stay' | 'contact' | 'policies' | 'checkout'
  ogTitle?: string;
  ogDescription?: string;
  ogImage?: string;
  ogType?: string;
  twitterCard?: string;
  canonical?: string;
  keywords?: string;
  robots?: string;
  referrer?: string;
  structuredData?: Record<string, any>[];
}

export interface SEOSettings {
  site_title: string;
  site_description: string;
  og_image_url: string;
  twitter_handle: string;
  page_titles: { [key: string]: string };
  meta_descriptions: { [key: string]: string };
}

export interface SiteInfoSettings {
  site_name: string;
  site_description: string;
  contact_email: string;
  contact_phone: string;
  address: string;
  social_links: { [key: string]: string };
  license_number?: string;
}

// Static SEO defaults - OPTIMIZED FOR TARGET KEYWORD RANKINGS
// Title: 50-60 chars | Meta Description: 120-160 chars
export const DEFAULT_SEO_SETTINGS: SEOSettings = {
  site_title: 'The Kualoa Beach House | Oahu Beachfront Vacation Rental',
  site_description: 'The Kualoa Beach House: book our beachfront Ka\'a\'awa vacation rental on Oahu with private beach access. Direct-book oceanfront stays near Kualoa Ranch and Mokoli\'i (Chinaman\'s Hat).',
  og_image_url: 'https://vacationrentaloahu.co/images/og-sharing-image.jpg',
  twitter_handle: '@vacationrentaloahu',
  page_titles: {
    // Each title is self-contained and already carries the brand, so nothing is appended.
    // Kept under ~60 chars so Google does not truncate them in results.
    // Category front-loaded, brand appended. GSC (16mo) shows the biggest single
    // block of unconverted demand is this exact phrasing - "oahu beachfront
    // vacation rentals" (389 impressions), "oahu vacation rentals" (289),
    // "oahu vacation rentals beachfront" (107), "oahu beachfront rentals" (80) -
    // all at zero clicks. Leading with the brand put the words those searchers
    // actually typed in the half of the title Google truncates, and the brand
    // name is only searched by people who already know us.
    home: 'Oahu Beachfront Vacation Rental | The Kualoa Beach House',
    booking: 'Book Oahu Beachfront Rental | The Kualoa Beach House',
    stay: "Oahu Beach House Rental in Ka'a'awa | The Kualoa Beach House",
    contact: 'Contact Us | The Kualoa Beach House',
    policies: 'Rental Policies | The Kualoa Beach House',
    checkout: 'Complete Reservation | Oahu Beachfront Rental',
    experiences: 'Oahu Experiences | The Kualoa Beach House',
    blog: 'Oahu Travel Guide | The Kualoa Beach House',
    faq: 'FAQ | The Kualoa Beach House',
    about: 'About Us | The Kualoa Beach House',
    kaaawa: "Ka'a'awa Vacation Rental Oahu | The Kualoa Beach House",
  },
  meta_descriptions: {
    home: 'Oahu vacation rentals, direct: book our beachfront Ka\'a\'awa beach house with private beach access near Kualoa Ranch and Mokoli\'i (Chinaman\'s Hat). Instant confirmation, no platform fees.',
    booking: 'Book an Oahu beachfront vacation rental direct: check availability on our oceanfront Ka\'a\'awa beach house. Secure checkout, instant confirmation, no platform fees.',
    stay: 'Oahu beach house rental in Ka\'a\'awa: our 3-bedroom beachfront oceanfront home with private beach access, photos, amenities, and a local area guide.',
    contact: 'Questions about our beachfront vacation rental in Oahu? Contact us for availability, amenities, and booking help. We respond within 24 hours.',
    policies: 'Review cancellation policy, house rules, and rental terms for our Ka\'a\'awa beachfront vacation rental on Oahu\'s windward coast.',
    checkout: 'Complete your Oahu beachfront rental booking. Secure payment for oceanfront beach house. Instant confirmation available.',
    experiences: 'Book Oahu tours and activities near our Ka\'a\'awa beachfront vacation rental: Kualoa Ranch, snorkeling, cultural tours, and island adventures.',
    faq: 'Answers about booking, check-in, amenities, and staying at our beachfront vacation rental on Oahu in Ka\'a\'awa near Kualoa Ranch.',
    about: 'Meet the hosts behind The Kualoa Beach House, a direct-book beachfront vacation rental in Ka\'a\'awa with local expertise and guest-first hospitality.',
    blog: 'Complete Oahu travel guide with expert articles on beaches, hiking, snorkeling, food, and trip planning from our Ka\'a\'awa home base.',
    kaaawa: "Ka'a'awa vacation rental on Oahu's windward coast. Beachfront beach house near Kualoa Ranch, Mokoli'i (Chinaman's Hat), and North Shore day trips. Local area guide.",
  }
};

export const DEFAULT_SEO = DEFAULT_SEO_SETTINGS;
export function useSEOSettings() {
  const { data, isLoading } = useQuery<SEOSettings>({
    queryKey: ['/api/settings/seo'],
    retry: false,
    staleTime: 5 * 60 * 1000,
  });
  return { seoSettings: data ?? DEFAULT_SEO_SETTINGS, isLoading };
}

// Enhanced hook for managing document head elements
/** Canonical brand string. Every page title either contains it or gets it appended. */
export const SITE_BRAND = 'The Kualoa Beach House';
export const PORTFOLIO_BRAND = 'Vacation Rental Oahu';

export function useSEO(props: SEOProps): ReactElement {
  const { seoSettings } = useSEOSettings();

  // Compute meta values at render time so they are available for SSR Helmet injection
  const pageTitle = props.title ||
    (props.pageType ? seoSettings.page_titles[props.pageType] : '') ||
    seoSettings.page_titles.home ||
    seoSettings.site_title || '';

  const pageDescription = props.description ||
    (props.pageType ? seoSettings.meta_descriptions[props.pageType] : '') ||
    seoSettings.site_description;

  const getAbsoluteImageUrl = (imageUrl: string | undefined): string => {
    if (!imageUrl) return seoSettings.og_image_url || '';
    const cleanUrl = imageUrl.replace(/^"+|"+$/g, '');
    if (cleanUrl.startsWith('http://') || cleanUrl.startsWith('https://')) return cleanUrl;
    return `${SITE_ORIGIN}${cleanUrl.startsWith('/') ? '' : '/'}${cleanUrl}`;
  };

  const ogImageUrl = getAbsoluteImageUrl(props.ogImage || seoSettings.og_image_url);
  // Companion tags: dimensions/type are only known for the default sharing image.
  // Compare normalized URLs (getAbsoluteImageUrl strips quotes / makes absolute)
  // so a page passing the same default image explicitly still counts as default.
  const isDefaultOgImage =
    ogImageUrl === getAbsoluteImageUrl(seoSettings.og_image_url) ||
    ogImageUrl === DEFAULT_SEO_SETTINGS.og_image_url;
  const ogImageAlt = isDefaultOgImage
    ? "The Kualoa Beach House, a beachfront vacation rental in Ka'a'awa, Oahu"
    : (props.ogTitle || pageTitle);

  // Append only the brand, never the full stored site title. Appending the whole
  // site_title produced duplicated, over-length titles whenever a page title was reworded
  // (e.g. "Oahu Vacation Rentals ... - Vacation Rental Oahu | Beachfront Beach House
  // Hawaii"). Checking for the brand keeps every title unique, on-brand, and short enough
  // that Google does not truncate it.
  // With more than one home published the site is Vacation Rental Oahu, not one
  // house; the suffix follows the brand the header and footer already show.
  const flags = useFeatureFlags();
  const [routerPath] = useLocation();
  const isPortfolio = flags.portfolioHomepage && flags.publishedHomeCount > 1;
  const brand = isPortfolio ? PORTFOLIO_BRAND : SITE_BRAND;
  // Shared pages (blog, guides, waiver, sitemap...) were titled with the first
  // home's name. With two homes that name only belongs on that home's own pages.
  const kualoaOwnPage = new RegExp(`^/(stay|booking|reviews|properties/${KUALOA_PROPERTY_SLUG})(/|$)`).test(routerPath || "");
  const titledPage = isPortfolio && !kualoaOwnPage
    ? pageTitle.replace(/\s*\|\s*The Kualoa Beach House(?:,\s*Oahu)?\s*$/i, "")
    : pageTitle;
  // The brand is appended only when the whole title still fits what search
  // results display. A guide title that already runs long keeps its own words;
  // Google would otherwise cut the end off, and the end was the brand anyway.
  const withBrand = `${titledPage} | ${brand}`;
  const browserTitle = titledPage.toLowerCase().includes(brand.toLowerCase())
    ? titledPage
    : withBrand.length <= 62
      ? withBrand
      : titledPage;

  const canonicalUrl = useCanonicalUrl(props.canonical);

  // CSR: keep DOM mutation side-effects so non-SSR page loads stay in sync
  useEffect(() => {
    document.title = browserTitle;
    updateMetaTag('description', pageDescription);
    if (props.keywords) updateMetaTag('keywords', props.keywords);
    updateMetaTag('robots', props.robots || 'index, follow');
    if (props.referrer) {
      updateMetaTag('referrer', props.referrer);
    } else {
      document.querySelector('meta[name="referrer"]')?.remove();
    }
    updateMetaProperty('og:title', props.ogTitle || pageTitle);
    updateMetaProperty('og:description', props.ogDescription || pageDescription);
    updateMetaProperty('og:image', ogImageUrl);
    updateMetaProperty('og:image:secure_url', ogImageUrl);
    updateMetaProperty('og:image:alt', ogImageAlt);
    if (isDefaultOgImage) {
      updateMetaProperty('og:image:type', 'image/jpeg');
      updateMetaProperty('og:image:width', '1200');
      updateMetaProperty('og:image:height', '630');
    } else {
      // Dimensions/type are unknown for custom images — remove any stale tags
      // left behind after SPA navigation from a default-image page.
      removeMetaProperty('og:image:type');
      removeMetaProperty('og:image:width');
      removeMetaProperty('og:image:height');
    }
    updateMetaProperty('og:type', props.ogType || 'website');
    updateMetaProperty('og:url', canonicalUrl);
    updateMetaProperty('og:site_name', seoSettings.site_title);
    updateMetaName('twitter:card', props.twitterCard || 'summary_large_image');
    updateMetaName('twitter:title', props.ogTitle || pageTitle);
    updateMetaName('twitter:description', props.ogDescription || pageDescription);
    updateMetaName('twitter:image', ogImageUrl);
    updateMetaName('twitter:image:alt', ogImageAlt);
    updateMetaName('twitter:site', seoSettings.twitter_handle);
    updateMetaName('twitter:creator', seoSettings.twitter_handle);
    updateCanonical(canonicalUrl);
    if (props.structuredData && props.structuredData.length > 0) {
      updateStructuredData(props.structuredData);
    }
  }, [browserTitle, canonicalUrl, pageTitle, pageDescription, ogImageUrl, props, seoSettings]);

  // Return Helmet element so SSR renderer captures title/meta in server HTML
  return (
    <Helmet>
      <title>{browserTitle}</title>
      <meta name="description" content={pageDescription} />
      <link rel="canonical" href={canonicalUrl} />
      {props.keywords && <meta name="keywords" content={props.keywords} />}
      <meta name="robots" content={props.robots || 'index, follow'} />
      {props.referrer && <meta name="referrer" content={props.referrer} />}
      <meta property="og:title" content={props.ogTitle || pageTitle} />
      <meta property="og:description" content={props.ogDescription || pageDescription} />
      <meta property="og:image" content={ogImageUrl} />
      <meta property="og:image:secure_url" content={ogImageUrl} />
      {isDefaultOgImage && <meta property="og:image:type" content="image/jpeg" />}
      {isDefaultOgImage && <meta property="og:image:width" content="1200" />}
      {isDefaultOgImage && <meta property="og:image:height" content="630" />}
      <meta property="og:image:alt" content={ogImageAlt} />
      <meta property="og:type" content={props.ogType || 'website'} />
      <meta property="og:url" content={canonicalUrl} />
      <meta property="og:site_name" content={seoSettings.site_title} />
      <meta name="twitter:card" content={props.twitterCard || 'summary_large_image'} />
      <meta name="twitter:title" content={props.ogTitle || pageTitle} />
      <meta name="twitter:description" content={props.ogDescription || pageDescription} />
      <meta name="twitter:image" content={ogImageUrl} />
      <meta name="twitter:image:alt" content={ogImageAlt} />
      <meta name="twitter:site" content={seoSettings.twitter_handle} />
      {props.structuredData?.map((schema, i) => (
        <script key={i} type="application/ld+json">{JSON.stringify(schema)}</script>
      ))}
    </Helmet>
  );
}

// Helper function to update meta tags by name
function updateMetaTag(name: string, content: string) {
  let meta = document.querySelector(`meta[name="${name}"]`) as HTMLMetaElement;
  if (!meta) {
    meta = document.createElement('meta');
    meta.name = name;
    document.head.appendChild(meta);
  }
  meta.content = content;
}

// Helper function to update meta tags by property
function updateMetaProperty(property: string, content: string) {
  let meta = document.querySelector(`meta[property="${property}"]`) as HTMLMetaElement;
  if (!meta) {
    meta = document.createElement('meta');
    meta.setAttribute('property', property);
    document.head.appendChild(meta);
  }
  meta.content = content;
}

// Helper function to remove a meta tag by property (for tags that only apply conditionally)
function removeMetaProperty(property: string) {
  document.querySelector(`meta[property="${property}"]`)?.remove();
}

// Helper function to update meta tags by name attribute
function updateMetaName(name: string, content: string) {
  let meta = document.querySelector(`meta[name="${name}"]`) as HTMLMetaElement;
  if (!meta) {
    meta = document.createElement('meta');
    meta.name = name;
    document.head.appendChild(meta);
  }
  meta.content = content;
}

// Helper function to update canonical URL
function updateCanonical(url: string) {
  let canonical = document.querySelector('link[rel="canonical"]') as HTMLLinkElement;
  if (!canonical) {
    canonical = document.createElement('link');
    canonical.rel = 'canonical';
    document.head.appendChild(canonical);
  }
  canonical.href = url;
}

// Helper function to update structured data
function updateStructuredData(data: Record<string, any>[]) {
  // Remove existing structured data scripts
  const existingScripts = document.querySelectorAll('script[type="application/ld+json"]');
  existingScripts.forEach(script => script.remove());

  // Add new structured data
  data.forEach(schemaData => {
    const script = document.createElement('script');
    script.type = 'application/ld+json';
    script.textContent = JSON.stringify(schemaData, null, 2);
    document.head.appendChild(script);
  });
}

// Generate Organization structured data with dynamic database settings
/**
 * Parse a display address like "Ka'a'awa, HI 96730, United States" into schema
 * PostalAddress fields.
 *
 * Four schema generators previously did `address.split(',')` and assigned parts
 * positionally, which put "HI 96730" in addressRegion and "United States" in
 * postalCode. The Organization node published that to every page, so the same
 * business advertised two different addresses in a single document - the exact
 * NAP inconsistency that dilutes local ranking and confuses AI systems trying to
 * resolve the entity.
 *
 * Match on shape instead of position, and ignore anything that is neither a
 * state code nor a ZIP (country names, extra commas).
 */
/**
 * Parse the configured postal address, falling back to the property's real
 * location.
 *
 * Call sites must not pass their own `fallback`. Six of them used to: five
 * supplied a truncated locality of "Ka", and one supplied Honolulu 96815, which
 * is Waikiki - roughly 25 miles from Ka'a'awa. That fallback fires whenever
 * site settings are unavailable (an SSR render before the query resolves, or a
 * failed settings fetch), so those values were published as fact inside
 * LocalBusiness, Organization, and VacationRental JSON-LD.
 *
 * Location is the single most load-bearing attribute for this business. It is
 * what a search or answer engine matches against "where to stay near Kualoa
 * Ranch", and a wrong or meaningless addressLocality does not merely fail to
 * help - it teaches the wrong answer.
 */
export function parsePostalAddress(
  address?: string,
  fallback: { locality: string; region: string; postalCode: string } = {
    locality: "Ka'a'awa",
    region: "HI",
    postalCode: "96730",
  },
): { locality: string; region: string; postalCode: string } {
  if (!address) return fallback;
  const parts = address.split(",").map((x) => x.trim()).filter(Boolean);
  if (parts.length === 0) return fallback;
  let region = fallback.region;
  let postalCode = fallback.postalCode;
  for (const part of parts.slice(1)) {
    const both = part.match(/^([A-Za-z]{2})\s+(\d{5}(?:-\d{4})?)$/);
    if (both) { region = both[1].toUpperCase(); postalCode = both[2]; continue; }
    if (/^[A-Za-z]{2}$/.test(part)) { region = part.toUpperCase(); continue; }
    if (/^\d{5}(?:-\d{4})?$/.test(part)) { postalCode = part; continue; }
  }
  return { locality: parts[0] || fallback.locality, region, postalCode };
}

/**
 * The business name, used wherever an entity is named.
 *
 * The Organization node previously took its name from the SEO site title, which
 * produced "Vacation Rental Oahu | Beachfront Beach House Hawaii" - a title-tag
 * string, complete with pipe, rather than a business name. The LodgingBusiness
 * and VacationRental nodes on the same page said "The Kualoa Beach House", so a
 * single document named the same business two different ways. Local ranking and
 * AI entity resolution both depend on that name being identical everywhere it
 * appears, including on the Google Business Profile.
 */
export const BUSINESS_NAME = "The Kualoa Beach House";
export const BUSINESS_ALTERNATE_NAME = "Vacation Rental Oahu";

export function generateOrganizationSchema(seoSettings?: SEOSettings, siteInfoSettings?: SiteInfoSettings): Record<string, any> {
  const siteName = BUSINESS_NAME;
  const siteDescription = seoSettings?.site_description || siteInfoSettings?.site_description || "";
  
  // Parse address from site_info settings or use defaults
  const { locality: addressLocality, region: addressRegion, postalCode } =
    parsePostalAddress(siteInfoSettings?.address);
  
  // Every configured profile, not a fixed three. This previously named only
  // instagram/facebook/twitter, so an Airbnb, VRBO, or Google Business Profile
  // URL saved in settings was silently dropped from `sameAs` - exactly backwards,
  // since those listings are the ones that corroborate the business to search and
  // AI answer engines. The other two sameAs sites in this file already derive
  // generically; this one was the outlier.
  const socialLinks = Object.values(siteInfoSettings?.social_links || {}).filter(Boolean);
  
  return {
    "@context": "https://schema.org",
    "@type": "Organization",
    "name": siteName,
    "alternateName": BUSINESS_ALTERNATE_NAME,
    "description": siteDescription,
    "url": getOrigin(),
    "telephone": schemaTelephone(siteInfoSettings?.contact_phone),
    "email": schemaEmail(siteInfoSettings?.contact_email),
    "address": {
      "@type": "PostalAddress",
      "addressLocality": addressLocality,
      "addressRegion": addressRegion,
      "postalCode": postalCode,
      "addressCountry": "US"
    },
    ...(socialLinks.length > 0 && { "sameAs": socialLinks })
  };
}

// Generate enhanced VacationRental structured data with rich property details - OPTIMIZED FOR TARGET KEYWORDS
export function generateVacationRentalSchema(property: any, pricing?: any, seoSettings?: SEOSettings, siteInfoSettings?: SiteInfoSettings): Record<string, any> {
  const siteName = seoSettings?.site_title || siteInfoSettings?.site_name || "The Kualoa Beach House | Oahu Beachfront Vacation Rental";
  const siteDescription = seoSettings?.site_description || siteInfoSettings?.site_description || "Book a beachfront vacation rental in Oahu with private beach access. Direct-book our Ka'a'awa oceanfront beach house near Kualoa Ranch.";
  
  // Parse address from site_info settings or use defaults
  const { locality: addressLocality, region: addressRegion, postalCode } =
    parsePostalAddress(siteInfoSettings?.address);
  
  return {
    "@context": "https://schema.org",
    "@type": "VacationRental",
    "name": property?.title || "The Kualoa Beach House - Oahu Beachfront Vacation Rental",
    "alternateName": "Oahu Beachfront Vacation Rentals | Oceanfront Rentals Oahu | Oahu Beach Houses",
    "description": siteDescription,
    "url": `${getOrigin()}/booking`,
    "telephone": schemaTelephone(siteInfoSettings?.contact_phone),
    "email": schemaEmail(siteInfoSettings?.contact_email),
    "address": {
      "@type": "PostalAddress",
      "addressLocality": addressLocality,
      "addressRegion": addressRegion,
      "postalCode": postalCode,
      "addressCountry": "US"
    },
    "geo": getPropertyGeo(property),
    "identifier": buildIdentifier(`${getOrigin()}/#vacation-rental`),
    "numberOfRooms": property?.bedrooms || 3,
    "numberOfBedrooms": property?.bedrooms || 3,
    "numberOfBathroomsTotal": property?.bathrooms || 2,
    "occupancy": {
      "@type": "QuantitativeValue",
      "value": property?.max_guests || 8,
      "unitText": "person"
    },
    "image": getPropertyImages(property),
    "containsPlace": buildContainsPlace(property),
    "aggregateRating": buildAggregateRating(property),
    "review": buildReviewNodes(),
    "amenityFeature": [
      {
        "@type": "LocationFeatureSpecification",
        "name": "Private Beach Access",
        "value": true
      },
      {
        "@type": "LocationFeatureSpecification",
        "name": "Ocean View",
        "value": true
      },
      {
        "@type": "LocationFeatureSpecification",
        "name": "Beachfront Location",
        "value": true
      },
      {
        "@type": "LocationFeatureSpecification",
        "name": "Full Kitchen",
        "value": true
      },
      {
        "@type": "LocationFeatureSpecification",
        "name": "WiFi",
        "value": true
      },
      {
        "@type": "LocationFeatureSpecification",
        "name": "Parking",
        "value": true
      },
      {
        "@type": "LocationFeatureSpecification",
        "name": "Air Conditioning",
        "value": true
      },
      {
        "@type": "LocationFeatureSpecification",
        "name": "Washer/Dryer",
        "value": true
      },
      {
        "@type": "LocationFeatureSpecification",
        "name": "BBQ Grill",
        "value": true
      }
    ],
    ...(property?.check_in_time && { "checkinTime": property.check_in_time }),
    ...(property?.check_out_time && { "checkoutTime": property.check_out_time }),
    ...(property?.pets_allowed !== undefined && { "petsAllowed": property.pets_allowed }),
    ...(() => { const o = buildPropertyOffers(pricing, property); return o ? { "offers": o } : {}; })(),
    "keywords": "vacation rental oahu, oahu vacation rentals, oahu beachfront vacation rentals, oahu beach house, oahu beach houses, oceanfront rentals oahu, Ka'a'awa vacation rental, beachfront oahu beach house, family vacation oahu, saturday farmers market oahu nearby, premium vacation rental oahu"
  };
}

// Generate LodgingBusiness structured data for vacation rental - OPTIMIZED FOR TARGET KEYWORDS
export function generateLodgingBusinessSchema(property: any, seoSettings?: SEOSettings, siteInfoSettings?: SiteInfoSettings): Record<string, any> {
  const siteName = seoSettings?.site_title || siteInfoSettings?.site_name || "The Kualoa Beach House | Oahu Beachfront Vacation Rental";
  const siteDescription = seoSettings?.site_description || siteInfoSettings?.site_description || "Book a beachfront vacation rental in Oahu with private beach access. Direct-book our Ka'a'awa oceanfront beach house near Kualoa Ranch.";
  
  // Parse address from site_info settings or use defaults
  const { locality: addressLocality, region: addressRegion, postalCode } =
    parsePostalAddress(siteInfoSettings?.address);
  
  return {
    "@context": "https://schema.org",
    "@type": "LodgingBusiness",
    "name": property?.title || seoSettings?.page_titles?.stay || siteName,
    "description": siteDescription,
    "url": `${getOrigin()}/booking`,
    "telephone": schemaTelephone(siteInfoSettings?.contact_phone),
    "email": schemaEmail(siteInfoSettings?.contact_email),
    "address": {
      "@type": "PostalAddress",
      "addressLocality": addressLocality,
      "addressRegion": addressRegion,
      "postalCode": postalCode,
      "addressCountry": "US"
    },
    "geo": getPropertyGeo(property),
    "identifier": buildIdentifier(`${getOrigin()}/#lodging-business`),
    "image": getPropertyImages(property),
    "containsPlace": buildContainsPlace(property),
    "starRating": {
      "@type": "Rating",
      "ratingValue": property?.rating || PROPERTY_RATING.ratingValue,
      "bestRating": "5"
    },
    "aggregateRating": buildAggregateRating(property),
    "review": buildReviewNodes(),
    ...(property?.bedrooms && { "numberOfRooms": property.bedrooms }),
    ...(property?.max_guests && {
      "occupancy": {
        "@type": "QuantitativeValue",
        "value": property.max_guests,
        "unitText": "person"
      }
    }),
    "amenityFeature": [
      {
        "@type": "LocationFeatureSpecification", 
        "name": "Private Beach Access",
        "value": true
      },
      {
        "@type": "LocationFeatureSpecification",
        "name": "Oceanfront Location",
        "value": true
      },
      {
        "@type": "LocationFeatureSpecification",
        "name": "Stunning Ocean Views",
        "value": true
      },
      {
        "@type": "LocationFeatureSpecification",
        "name": "Full Kitchen",
        "value": true
      },
      {
        "@type": "LocationFeatureSpecification",
        "name": "High-Speed WiFi",
        "value": true
      },
      {
        "@type": "LocationFeatureSpecification",
        "name": "Free Parking",
        "value": true
      },
      {
        "@type": "LocationFeatureSpecification",
        "name": "Air Conditioning",
        "value": true
      },
      {
        "@type": "LocationFeatureSpecification",
        "name": "Washer & Dryer",
        "value": true
      },
      {
        "@type": "LocationFeatureSpecification",
        "name": "BBQ Grill",
        "value": true
      },
      {
        "@type": "LocationFeatureSpecification",
        "name": "Beach Equipment",
        "value": true
      }
    ],
    ...(property?.check_in_time && { "checkinTime": property.check_in_time }),
    ...(property?.check_out_time && { "checkoutTime": property.check_out_time }),
    ...(property?.pets_allowed !== undefined && { "petsAllowed": property.pets_allowed }),
    ...(property?.smoking_allowed !== undefined && { "smokingAllowed": property.smoking_allowed }),
    ...(property?.price_range && { "priceRange": property.price_range })
  };
}

// Generate enhanced Review structured data with vacation rental specifics
export function generateReviewsSchema(reviews: any[], property?: any, seoSettings?: SEOSettings): Record<string, any>[] {
  if (!reviews || reviews.length === 0) {
    return [];
  }

  return reviews.slice(0, 5).map(review => ({
    "@context": "https://schema.org",
    "@type": "Review",
    "reviewBody": review.review_text || review.content,
    "reviewRating": {
      "@type": "Rating",
      "ratingValue": review.rating.toString(),
      "bestRating": "5",
      "worstRating": "1"
    },
    "author": {
      "@type": "Person",
      "name": review.guest_name
    },
    "datePublished": (review.review_date || review.created_at)?.split('T')[0],
    "publisher": {
      "@type": "Organization",
      "name": "The Kualoa Beach House"
    },
    "itemReviewed": {
      "@type": "VacationRental",
      "name": property?.title || "The Kualoa Beach House - 3-Bedroom Oceanfront Vacation Rental, Oahu Hawaii",
      "description": "Premium beachfront vacation rental in Oahu with private beach access and premium amenities",
      "address": {
        "@type": "PostalAddress",
        "addressLocality": "Ka'a'awa",
        "addressRegion": "HI",
        "addressCountry": "US"
      }
    }
  }));
}

// Generate BreadcrumbList structured data
export function generateBreadcrumbSchema(breadcrumbs: Array<{name: string, url: string}>): Record<string, any> {
  return {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    "itemListElement": breadcrumbs.map((crumb, index) => ({
      "@type": "ListItem",
      "position": index + 1,
      "name": crumb.name,
      "item": `https://vacationrentaloahu.co${crumb.url}`
    }))
  };
}

// Generate enhanced Product structured data for booking page with vacation rental specifics
export function generateProductSchema(property: any, pricing: any, seoSettings?: SEOSettings): Record<string, any> {
  const siteName = seoSettings?.site_title || "The Kualoa Beach House | Beachfront Vacation Rental Oahu Hawaii";
  const siteDescription = seoSettings?.site_description || "Book a beachfront vacation rental in Oahu with private beach access. Direct-book our Ka'a'awa oceanfront beach house near Kualoa Ranch.";
  
  return {
    "@context": "https://schema.org",
    "@type": "Product",
    "name": property?.title || seoSettings?.page_titles?.booking || "The Kualoa Beach House - 3-Bedroom Oceanfront Vacation Rental, Oahu Hawaii",
    "description": seoSettings?.meta_descriptions?.booking || siteDescription,
    "image": getPropertyImages(property),
    "brand": {
      "@type": "Brand",
      "name": "Vacation Rental Oahu"
    },
    "category": "Vacation Rental",
    // No rating and no reviews here, and no additionalType pointing back at
    // VacationRental.
    //
    // This node sits on /booking next to the knowledge graph, which already
    // publishes the rental with its rating. Carrying a second copy of the same
    // aggregateRating and the same two review nodes, under a type that
    // declared itself a VacationRental and had no @id to tell it apart, gave
    // Google one entity with two ratings. /booking was the only page still
    // reporting four reviews instead of two after the graph was untangled.
    //
    // The merchant listing does not need the rating: it is built from offers,
    // price and availability, all of which stay. The rich result that should
    // carry the stars for a lodging is the VacationRental one.
    "@id": `${getOrigin()}/booking#product`,
    ...(() => { const o = buildPropertyOffers(pricing, property); return o ? { "offers": o } : {}; })()
  };
}

// Enhanced location-based keywords for SEO optimization - EXACT MATCH TARGET PHRASES
export const LOCATION_KEYWORDS = {
  // Primary target keywords (exact match phrases user wants to rank #1 for)
  primary: 'vacation rental oahu, oahu vacation rentals, oahu beach house, oahu beach houses, oceanfront rentals oahu',
  // Per-page primary clusters, deliberately non-overlapping to prevent cannibalization
  // across home / stay / booking. Prioritized from GSC impressions + Ahrefs volume:
  // "oahu vacation rentals" (900 vol, KD5) -> home; "beach house rentals oahu" (250) -> stay;
  // "oahu beachfront vacation rentals" (100) + book-direct intent -> booking.
  homePrimary: 'oahu vacation rentals, vacation rental oahu, oahu vacation home rental, vacation rentals oahu hawaii',
  stayPrimary: 'oahu beach house, oahu beach house rental, beach house rentals oahu, beachfront beach house oahu, oahu beach rentals',
  bookingPrimary: 'oahu beachfront vacation rentals, oceanfront rentals oahu, oahu oceanfront rentals, beachfront vacation rentals oahu, book oahu vacation rental direct',
  // Premium keywords (important ranking targets)
  premium: 'oahu beachfront vacation rentals, premium vacation rental oahu, stunning oahu beach house, beachfront oahu vacation rental',
  // Beachfront/oceanfront keywords
  beachfront: 'oahu beachfront vacation rentals, beachfront vacation rental oahu, oceanfront vacation rental oahu, beachfront oahu beach house',
  // Location-specific
  locations: "Ka'a'awa vacation rental, North Shore beach house, Windward Coast rental, Ka'a'awa beach house, Mokoli'i (Chinaman's Hat) vacation rental",
  // Amenities and features
  amenities: 'beachfront property oahu, private beach access hawaii, ocean view rental oahu, premium amenities vacation rental',
  // Activities and experiences
  activities: 'family vacation oahu, group accommodation hawaii, Hawaiian getaway, beach vacation oahu, water sports rental',
  // Nearby attractions for local SEO
  attractions: 'near Kualoa Ranch, close to Polynesian Cultural Center, Diamond Head area, Hanauma Bay nearby, saturday farmers market oahu'
};

// Legacy SEO meta constants (keywords / default copy helpers)
export const LEGACY_SEO_META = {
  siteName: 'The Kualoa Beach House | Oahu Beachfront Vacation Rental | VacationRentalOahu.co',
  defaultImage: 'https://vacationrentaloahu.co/images/og-sharing-image.jpg',
  twitterHandle: '@vacationrentaloahu',
  facebookAppId: '',
  defaultKeywords: "vacation rental oahu, oahu vacation rentals, oahu beachfront vacation rentals, oahu beach house, oahu beach houses, oceanfront rentals oahu, saturday farmers market oahu, Ka'a'awa beach house, Ka'a'awa vacation rental, premium vacation rental oahu",
  defaultDescription: 'Book a beachfront vacation rental in Oahu with private beach access. Direct-book our Ka\'a\'awa oceanfront beach house near Kualoa Ranch and Mokoli\'i (Chinaman\'s Hat).'
};

// Generate enhanced Place structured data for location-based SEO - OPTIMIZED FOR TARGET KEYWORDS
export function generatePlaceSchema(property: any, seoSettings?: SEOSettings, siteInfoSettings?: SiteInfoSettings): Record<string, any> {
  const { locality: addressLocality, region: addressRegion, postalCode } =
    parsePostalAddress(siteInfoSettings?.address);
  
  return {
    '@context': 'https://schema.org',
    '@type': 'Place',
    'name': 'The Kualoa Beach House - Oahu Beachfront Vacation Rental near Kualoa Ranch',
    'alternateName': 'Oahu Beachfront Vacation Rentals | Oceanfront Rentals Oahu | Oahu Beach Houses',
    'description': "Stunning beachfront vacation rental Oahu with private beach access - premium oceanfront oahu beach house in beautiful Ka'a'awa, near Saturday farmers markets and Kualoa Ranch",
    'address': {
      '@type': 'PostalAddress',
      'addressLocality': addressLocality,
      'addressRegion': addressRegion,
      'postalCode': postalCode,
      'addressCountry': 'US'
    },
    ...(property?.lat && property?.lng && {
      'geo': {
        '@type': 'GeoCoordinates',
        'latitude': parseFloat(property.lat),
        'longitude': parseFloat(property.lng)
      }
    }),
    'amenityFeature': [
      'Premium Vacation Rental',
      'Private Beach Access',
      'Oceanfront Views',
      'Beachfront Location',
      'Premium Amenities',
      'Full Kitchen',
      'Free Parking',
      'High-Speed WiFi'
    ],
    'keywords': LOCATION_KEYWORDS.primary + ', ' + LOCATION_KEYWORDS.premium + ', ' + LOCATION_KEYWORDS.beachfront + ', ' + LOCATION_KEYWORDS.locations
  };
}

// Generate TouristTrip structured data for experiences page - helps with AI/LLM search visibility
export function generateTouristTripSchema(): Record<string, any> {
  return {
    '@context': 'https://schema.org',
    '@type': 'TouristTrip',
    'name': 'Oahu Hawaiian Adventures & Experiences',
    'description': 'Discover authentic Hawaiian experiences from your Oahu vacation rental. Snorkeling, hiking, cultural tours, water sports, and unforgettable island adventures.',
    'touristType': ['Family', 'Couples', 'Adventure Seekers', 'Beach Lovers'],
    'itinerary': {
      '@type': 'ItemList',
      'itemListElement': [
        {
          '@type': 'ListItem',
          'position': 1,
          'name': 'Beach & Water Activities',
          'description': 'Snorkeling, surfing, kayaking, paddleboarding, and swimming at Oahu\'s best beaches'
        },
        {
          '@type': 'ListItem',
          'position': 2,
          'name': 'Hiking & Nature',
          'description': 'Explore Diamond Head, Koko Head, Manoa Falls, and scenic windward coast trails'
        },
        {
          '@type': 'ListItem',
          'position': 3,
          'name': 'Cultural Experiences',
          'description': 'Visit Polynesian Cultural Center, Kualoa Ranch tours, and authentic Hawaiian luaus'
        },
        {
          '@type': 'ListItem',
          'position': 4,
          'name': 'Food Tours',
          'description': 'Hawaiian cuisine, local food trucks, farmers markets, and authentic island dining'
        }
      ]
    },
    'provider': {
      '@type': 'Organization',
      'name': 'The Kualoa Beach House',
      'url': 'https://vacationrentaloahu.co'
    },
    'offers': {
      '@type': 'AggregateOffer',
      'priceCurrency': 'USD',
      'lowPrice': '50',
      'highPrice': '500',
      'offerCount': '100+',
      'url': 'https://vacationrentaloahu.co/experiences'
    }
  };
}

// Generate TravelAction structured data for experiences - enhances search relevance
export function generateTravelActionSchema(): Record<string, any> {
  return {
    '@context': 'https://schema.org',
    '@type': 'TravelAction',
    'name': 'Book Hawaiian Experiences & Activities',
    'description': 'Secure booking for Oahu adventures including snorkeling, hiking, cultural tours, and water sports. All activities backed by customer satisfaction guarantee.',
    'agent': {
      '@type': 'Organization',
      'name': 'The Kualoa Beach House',
      'url': 'https://vacationrentaloahu.co'
    },
    'target': {
      '@type': 'EntryPoint',
      'urlTemplate': 'https://vacationrentaloahu.co/experiences',
      'actionPlatform': ['http://schema.org/DesktopWebPlatform', 'http://schema.org/MobileWebPlatform']
    },
    'result': {
      '@type': 'TravelAction',
      'location': {
        '@type': 'Place',
        'name': 'Oahu, Hawaii',
        'address': {
          '@type': 'PostalAddress',
          'addressLocality': 'Oahu',
          'addressRegion': 'HI',
          'addressCountry': 'US'
        }
      }
    }
  };
}

// Generate LocalBusiness structured data for contact page
export function generateLocalBusinessSchema(seoSettings?: SEOSettings, siteInfoSettings?: SiteInfoSettings): Record<string, any> {
  const { locality: addressLocality, region: addressRegion, postalCode } =
    parsePostalAddress(siteInfoSettings?.address);
  const localBusinessSameAs = Object.values(siteInfoSettings?.social_links || {}).filter(Boolean);
  
  return {
    '@context': 'https://schema.org',
    '@type': 'LocalBusiness',
    '@id': 'https://vacationrentaloahu.co/#business',
    'name': siteInfoSettings?.site_name || 'The Kualoa Beach House',
    'description': 'Premium beachfront vacation rental in Oahu, Hawaii. Direct booking for oceanfront beach house with private beach access near Kualoa Ranch.',
    'url': 'https://vacationrentaloahu.co',
    'telephone': schemaTelephone(siteInfoSettings?.contact_phone),
    'email': schemaEmail(siteInfoSettings?.contact_email),
    'address': {
      '@type': 'PostalAddress',
      'addressLocality': addressLocality,
      'addressRegion': addressRegion,
      'postalCode': postalCode,
      'addressCountry': 'US'
    },
    'geo': {
      '@type': 'GeoCoordinates',
      'latitude': 21.5136,
      'longitude': -157.8453
    },
    'openingHoursSpecification': {
      '@type': 'OpeningHoursSpecification',
      'dayOfWeek': ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'],
      'opens': '00:00',
      'closes': '23:59'
    },
    'priceRange': '$$$',
    'currenciesAccepted': 'USD',
    'paymentAccepted': 'Credit Card, Debit Card',
    'areaServed': {
      '@type': 'Place',
      'name': 'Oahu, Hawaii'
    },
    // Omit the property entirely when nothing is configured. `sameAs: []` is not
    // neutral - it publishes an explicit claim that the entity has no other
    // presence anywhere, which is both false and unhelpful to an answer engine.
    ...(localBusinessSameAs.length > 0 && { 'sameAs': localBusinessSameAs })
  };
}

// Generate FAQPage structured data for rich snippets
export function generateFAQSchema(faqs: Array<{question: string, answer: string}>): Record<string, any> {
  return {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    'mainEntity': faqs.map(faq => ({
      '@type': 'Question',
      'name': faq.question,
      'acceptedAnswer': {
        '@type': 'Answer',
        'text': faq.answer
      }
    }))
  };
}

// Generate ItemList structured data for category/list pages
export function generateItemListSchema(
  items: Array<{name: string, url: string, description?: string, position?: number}>,
  listName: string
): Record<string, any> {
  return {
    '@context': 'https://schema.org',
    '@type': 'ItemList',
    'name': listName,
    'itemListElement': items.map((item, index) => ({
      '@type': 'ListItem',
      'position': item.position || index + 1,
      'name': item.name,
      'url': item.url.startsWith('http') ? item.url : `https://vacationrentaloahu.co${item.url}`,
      ...(item.description && { 'description': item.description })
    }))
  };
}

// Generate CollectionPage structured data for blog category pages
export function generateCollectionPageSchema(
  categoryName: string,
  categorySlug: string,
  description: string,
  postCount: number
): Record<string, any> {
  return {
    '@context': 'https://schema.org',
    '@type': 'CollectionPage',
    'name': `${categoryName} - Oahu Travel Guide`,
    'description': description,
    'url': `https://vacationrentaloahu.co/blog/category/${categorySlug}`,
    'isPartOf': {
      '@type': 'WebSite',
      'name': 'The Kualoa Beach House',
      'url': 'https://vacationrentaloahu.co'
    },
    'numberOfItems': postCount,
    'mainEntity': {
      '@type': 'ItemList',
      'name': categoryName,
      'numberOfItems': postCount
    }
  };
};

// Stable entity @id URIs for linked knowledge graph
export const ENTITY_IDS = {
  organization: `${SITE_ORIGIN}/#organization`,
  website: `${SITE_ORIGIN}/#website`,
  vacationRental: `${SITE_ORIGIN}/#vacation-rental`,
  placeKaaawa: `${SITE_ORIGIN}/#place-kaaawa`,
  lodgingBusiness: `${SITE_ORIGIN}/#lodging-business`,
  personHost: `${SITE_ORIGIN}/#person-host`,
} as const;

export interface KnowledgeGraphInput {
  property?: any;
  pricing?: any;
  seoSettings?: SEOSettings;
  siteInfoSettings?: SiteInfoSettings;
  host?: { name: string; bio?: string; image?: string; jobTitle?: string };
}

export function generatePersonSchema(host: {
  name: string;
  bio?: string;
  image?: string;
  jobTitle?: string;
}): Record<string, any> {
  return {
    '@type': 'Person',
    '@id': ENTITY_IDS.personHost,
    name: host.name,
    jobTitle: host.jobTitle || 'Property Host',
    description: host.bio,
    worksFor: { '@id': ENTITY_IDS.organization },
    knowsAbout: ["Oahu travel", "Vacation rentals", "Ka'a'awa", 'Hawaiian hospitality'],
    ...(host.image && { image: host.image }),
    url: `${SITE_ORIGIN}/about`,
  };
}

export function generateKnowledgeGraphSchema(input: KnowledgeGraphInput = {}): Record<string, any> {
  const { property, pricing, seoSettings, siteInfoSettings, host } = input;
  // Entity nodes are named with the business name, never the SEO title. Deriving
  // this from seoSettings.site_title meant the Organization and LodgingBusiness
  // announced themselves as "Vacation Rental Oahu | Beachfront Beach House
  // Hawaii" - a title tag, pipe included - while sibling nodes in the same graph
  // said "The Kualoa Beach House". It also meant that retitling a page silently
  // renamed the business: front-loading the homepage title earlier today pushed
  // "Oahu Beachfront Vacation Rental" into the graph as an entity name.
  const siteName = BUSINESS_NAME;
  const siteDescription = seoSettings?.site_description || siteInfoSettings?.site_description || '';
  const { locality: addressLocality, region: addressRegion, postalCode } =
    parsePostalAddress(siteInfoSettings?.address);
  const phone = schemaTelephone(siteInfoSettings?.contact_phone);
  const email = schemaEmail(siteInfoSettings?.contact_email);
  const socialLinks = Object.values(siteInfoSettings?.social_links || {}).filter(Boolean);

  const images = getPropertyImages(property);
  const geo = getPropertyGeo(property);
  const aggregateRating = buildAggregateRating(property);
  const reviewNodes = buildReviewNodes();
  const containsPlace = buildContainsPlace(property);
  // Fall back to the business name, not a generic description. Pages that do not
  // load property data (/about, /kaaawa, /faq, /policies) were naming the
  // VacationRental entity "Oahu Beachfront Vacation Rental", so four pages
  // published a different name for the same @id than the pages that do load it.
  const propertyName = property?.title || BUSINESS_NAME;

  const graph: Record<string, any>[] = [
    {
      '@type': 'Organization',
      '@id': ENTITY_IDS.organization,
      name: siteName,
      description: siteDescription,
      url: SITE_ORIGIN,
      telephone: phone,
      email,
      image: images,
      logo: `${SITE_ORIGIN}/images/og-sharing-image.jpg`,
      identifier: buildIdentifier(ENTITY_IDS.organization),
      address: {
        '@type': 'PostalAddress',
        addressLocality,
        addressRegion,
        postalCode,
        addressCountry: 'US',
      },
      ...(socialLinks.length > 0 && { sameAs: socialLinks }),
    },
    {
      '@type': 'WebSite',
      '@id': ENTITY_IDS.website,
      name: siteName,
      url: SITE_ORIGIN,
      publisher: { '@id': ENTITY_IDS.organization },
    },
    {
      '@type': 'Place',
      '@id': ENTITY_IDS.placeKaaawa,
      additionalType: 'https://schema.org/TouristDestination',
      name: "Ka'a'awa, Oahu | Windward Coast",
      description: 'Beachfront vacation rental location on Oahu\'s scenic windward coast, near Kualoa Ranch and Mokoli\'i (Chinaman\'s Hat).',
      image: images,
      identifier: buildIdentifier(ENTITY_IDS.placeKaaawa),
      address: {
        '@type': 'PostalAddress',
        addressLocality: "Ka'a'awa",
        addressRegion: 'HI',
        postalCode: '96730',
        addressCountry: 'US',
      },
      geo,
    },
    {
      // The operating business, not the thing being rated.
      //
      // This node used to carry `additionalType: schema.org/VacationRental`
      // while the VacationRental node pointed back with
      // `additionalType: schema.org/LodgingBusiness`. That circular pair told
      // Google the two were one entity, so it merged them and then found two
      // of everything: two `geo` values ("Duplicate field geo"), two
      // identifiers, and two `aggregateRating` nodes wrapping the same two
      // reviews. That last one is what put 60 items under "Review has multiple
      // aggregate ratings" in Search Console.
      //
      // The rating, the reviews, the coordinates and the unit description now
      // live on the VacationRental node alone. This one keeps what actually
      // belongs to a business: how to contact it, who owns it, where it
      // operates. It is the provider, and the rental points at it as such.
      '@type': 'LodgingBusiness',
      '@id': ENTITY_IDS.lodgingBusiness,
      name: property?.title || siteName,
      description: siteDescription,
      url: `${SITE_ORIGIN}/stay`,
      telephone: phone,
      email,
      image: images,
      identifier: buildIdentifier(ENTITY_IDS.lodgingBusiness),
      address: {
        '@type': 'PostalAddress',
        addressLocality: "Ka'a'awa",
        addressRegion: 'HI',
        postalCode: '96730',
        addressCountry: 'US',
      },
      location: { '@id': ENTITY_IDS.placeKaaawa },
      numberOfRooms: property?.bedrooms || 3,
      petsAllowed: property?.pets_allowed ?? false,
      priceRange: '$$$',
      parentOrganization: { '@id': ENTITY_IDS.organization },
    },
    {
      // The listing a guest books, and the only node in this graph that
      // publishes a rating. Google's VacationRental rich result reads this one.
      '@type': 'VacationRental',
      '@id': ENTITY_IDS.vacationRental,
      name: propertyName,
      description: siteDescription,
      url: `${SITE_ORIGIN}/booking`,
      telephone: phone,
      email,
      image: images,
      // Google wants this unique to the listing, so it is the property's own
      // stored id rather than the shared one every node used to publish.
      identifier: buildIdentifier(PROPERTY_ID, 'VacationRentalID'),
      address: {
        '@type': 'PostalAddress',
        addressLocality: "Ka'a'awa",
        addressRegion: 'HI',
        postalCode: '96730',
        addressCountry: 'US',
      },
      geo,
      containsPlace,
      numberOfBedrooms: property?.bedrooms || 3,
      numberOfBathroomsTotal: property?.bathrooms || 2,
      occupancy: {
        '@type': 'QuantitativeValue',
        value: property?.max_guests || 8,
        unitText: 'person',
      },
      amenityFeature: buildAmenityFeatures(),
      aggregateRating,
      review: reviewNodes,
      location: { '@id': ENTITY_IDS.placeKaaawa },
      provider: { '@id': ENTITY_IDS.lodgingBusiness },
      ...(property?.check_in_time && { checkinTime: property.check_in_time }),
      ...(property?.check_out_time && { checkoutTime: property.check_out_time }),
      // Same builder as the other price-bearing nodes: this one used to publish a
      // bare Offer with no promotional pricing, so the graph could describe the
      // property at one rate while the page advertised another.
      ...(() => { const o = buildPropertyOffers(pricing, property); return o ? { offers: o } : {}; })(),
    },
  ];

  if (host?.name) {
    graph.push(generatePersonSchema(host));
  }

  return {
    '@context': 'https://schema.org',
    '@graph': graph,
  };
}

export function generateSpeakableSchema(selectors: string[]): Record<string, any> {
  return {
    '@context': 'https://schema.org',
    '@type': 'WebPage',
    speakable: {
      '@type': 'SpeakableSpecification',
      cssSelector: selectors,
    },
  };
}